import { Alert, TextLink, Text, Box } from 'braid-design-system';
import { DateTime } from 'luxon';

import { ContractStatus } from 'src/generated/graphql';
import type { ClientListingForListingsTab } from 'src/types/graphql-derived';
import { ConsoleErrorId, consoleErrorWrapper } from 'src/utils';

// Show base contract is expiring alert when there is 31 days or less remaining
const SHOW_BASE_CONTRACT_EXPIRING_ALERT_THRESHOLD_DAYS = 31;

export interface AdAlertNotificationExpiringProps {
  listing: ClientListingForListingsTab;
}

const numberOfDaysBetweenDates = (startDate: Date, endDate: Date) =>
  Math.ceil(
    DateTime.fromJSDate(endDate).diff(DateTime.fromJSDate(startDate), 'days')
      .days,
  );

const AdAlertNotificationExpiring = ({
  listing,
}: AdAlertNotificationExpiringProps) => {
  if (listing.baseContract === null) {
    return null;
  }

  if (listing.baseContract.status !== ContractStatus.Active) {
    // Only handle Active status in this component
    return null;
  }

  if (!listing.baseContract.expiryDate) {
    // Check for data corruption - if contract status is Active, expiryDate must be present
    consoleErrorWrapper(
      ConsoleErrorId.AdAlertNotificationBaseContractValidation,
      `Listing ID ${listing.id} has base contract with Active status but missing expiryDate`,
    );
    return null;
  }

  const listingBaseContractExpiryDate = new Date(
    listing.baseContract.expiryDate,
  ).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const expiringFromNowInDays = numberOfDaysBetweenDates(
    new Date(),
    new Date(listing.baseContract.expiryDate),
  );

  /**
   * Show base contract expiring info alert when:
   * - base contract status is Active
   * - expiry date is within threshold of 31 days from today
   */
  const showListingBaseContractExpiringInfoAlert = Boolean(
    expiringFromNowInDays <= SHOW_BASE_CONTRACT_EXPIRING_ALERT_THRESHOLD_DAYS,
  );

  if (showListingBaseContractExpiringInfoAlert) {
    return (
      <Box data-testid="ad-alert-expiring-notification-container">
        <Alert tone="info">
          <Text>
            Your {listing.baseContract.productTitle} ad will expire on{' '}
            {listingBaseContractExpiryDate}. If you need help planning the next
            steps for advertising your business, call us on 13 73 35 or email{' '}
            <TextLink href="mailto:enquiries@seekbusiness.com.au">
              enquiries@seekbusiness.com.au
            </TextLink>
            .
          </Text>
        </Alert>
      </Box>
    );
  }

  return null;
};

export default AdAlertNotificationExpiring;
